import { useRef, useState } from 'react';

import { Card, Carousel, Icons, Image, theme } from '@contiple/antd-ds';
import type { CarouselRef } from 'antd/es/carousel';

import type { BannerItem } from '@/types/dashboard';

interface BannerCarouselProps {
  banners: BannerItem[];
}

const ARROW_SIZE = 32;

export default function BannerCarousel({ banners }: BannerCarouselProps) {
  const carouselRef = useRef<CarouselRef>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { token } = theme.useToken();

  if (banners.length === 0) return null;

  const arrowBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: ARROW_SIZE,
    height: ARROW_SIZE,
    borderRadius: '50%',
    backgroundColor: token.colorBgContainer,
    border: 'none',
    boxShadow: token.boxShadowSecondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    zIndex: 1,
  };

  return (
    <Card
      variant="borderless"
      style={{
        width: 386,
        flexShrink: 0,
        position: 'relative',
        boxShadow: 'none',
      }}
      styles={{ body: { padding: 0 } }}>
      <Carousel
        ref={carouselRef}
        autoplay
        autoplaySpeed={5000}
        arrows={false}
        dots={false}
        afterChange={setCurrentSlide}
        style={{ height: 266 }}>
        {banners.map((banner) => (
          <a
            key={banner.id}
            href={banner.linkUrl}
            target="_blank"
            rel="noreferrer">
            <Image
              src={banner.imageUrl}
              alt={banner.title}
              preview={false}
              width="100%"
              height={266}
              style={{ objectFit: 'cover' }}
            />
          </a>
        ))}
      </Carousel>

      <button
        type="button"
        aria-label="previous"
        onClick={() => carouselRef.current?.prev()}
        style={{ ...arrowBaseStyle, left: 12 }}>
        <Icons.ChevronLeft size={16} color={token.colorText} />
      </button>

      <button
        type="button"
        aria-label="next"
        onClick={() => carouselRef.current?.next()}
        style={{ ...arrowBaseStyle, right: 12 }}>
        <Icons.ChevronRight size={16} color={token.colorText} />
      </button>

      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: token.colorWhite,
          padding: '2px 10px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.5,
        }}>
        {currentSlide + 1}/{banners.length}
      </div>
    </Card>
  );
}
